import { useCallback, useState } from 'react'
import { useForm, zodResolver } from '@mantine/form'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  InputWrapper,
  NumberInput,
  Radio,
  Stack,
} from '@mantine/core'
import { IconMinus } from '@tabler/icons-react'
import { Berth, EditBerthFormSchema, EditBerthFormType } from '../models/marina'
import { useEditBerth } from '../api/marina'
import { useQueryClient } from '@tanstack/react-query'
import { useMediaQuery } from '@mantine/hooks'

interface Props {
  berth: Berth
  onCancel: () => void
}

export function EditBerthForm({ berth, onCancel }: Props) {
  const [pricing, setPricing] = useState<'flat_fee' | 'per_meter'>(
    berth.pricing
  )
  const queryClient = useQueryClient()
  const { mutate: editBerth, isPending } = useEditBerth()
  const isSmallScreen = useMediaQuery('(max-width: 640px)')

  const form = useForm<EditBerthFormType>({
    initialValues: {
      min_length: berth.min_length,
      max_length: berth.max_length,
      name: berth.name,
      boat_types: berth.boat_types,
      pricing: berth.pricing,
      price_per_meter:
        berth.pricing === 'per_meter' ? parseFloat(berth.price) : 0,
      price_flat_fee:
        berth.pricing === 'flat_fee' ? parseFloat(berth.price) : 0,
    },
    validate: zodResolver(EditBerthFormSchema),
  })

  const handleSubmit = useCallback(
    async (values: EditBerthFormType) => {
      if (berth.marina_id) {
        editBerth(
          {
            berthsData: values,
            marinaId: berth.marina_id,
            pricing: pricing,
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: ['berths-by-period'] })
              form.reset()
              onCancel()
            },
          }
        )
      }
    },
    [berth, queryClient, pricing, editBerth, onCancel, form]
  )

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <InputWrapper
          id="min_length"
          label="Boat size (length in meters)"
          error={[form.errors.min_length, form.errors.max_length]
            .filter(Boolean)
            .join(' ')}
        >
          {isSmallScreen ? (
            <Stack align="flex-start" mb={5}>
              <NumberInput
                flex="1"
                placeholder="Min"
                description="Min size"
                required
                min={0}
                maw={310}
                w="100%"
                allowDecimal={false}
                {...form.getInputProps('min_length', { withError: false })}
              />
              <NumberInput
                placeholder="Max"
                description="Max size"
                flex="1"
                required
                min={0}
                maw={310}
                w="100%"
                allowDecimal={false}
                {...form.getInputProps('max_length', { withError: false })}
              />
            </Stack>
          ) : (
            <Group align="flex-end" mb={5}>
              <NumberInput
                flex="1"
                placeholder="Min"
                description="Min size"
                required
                min={0}
                allowDecimal={false}
                {...form.getInputProps('min_length', { withError: false })}
              />
              <Box h={32}>
                <IconMinus />
              </Box>
              <NumberInput
                placeholder="Max"
                description="Max size"
                flex="1"
                required
                min={0}
                allowDecimal={false}
                {...form.getInputProps('max_length', { withError: false })}
              />
            </Group>
          )}
        </InputWrapper>

        <Checkbox.Group
          value={form.values.boat_types}
          onChange={(value) => form.setFieldValue('boat_types', value)}
          label="Compatible boat types"
        >
          <Group mt="xs">
            <Checkbox value="motorboat" label="Motorboat" />
            <Checkbox value="sailboat" label="Sailboat" />
            <Checkbox value="catamaran" label="Catamaran" />
          </Group>
        </Checkbox.Group>

        <Divider />

        <InputWrapper
          id="price"
          label="Price"
          description="Select which pricing method will be used for these berths"
        >
          <Radio.Group
            value={pricing}
            onChange={(value) => setPricing(value as 'per_meter' | 'flat_fee')}
          >
            <Stack pt={20} gap="xs">
              <Group>
                <NumberInput
                  hideControls
                  disabled={pricing !== 'per_meter'}
                  placeholder="0.00"
                  w={80}
                  {...form.getInputProps('price_per_meter')}
                />
                <Radio value="per_meter" label="Price per meter per day (€)" />
              </Group>

              <Group>
                <NumberInput
                  hideControls
                  placeholder="0.00"
                  disabled={pricing !== 'flat_fee'}
                  w={80}
                  {...form.getInputProps('price_flat_fee')}
                />
                <Radio value="flat_fee" label="Flat fee per day (€)" />
              </Group>
            </Stack>
          </Radio.Group>
        </InputWrapper>

        <Divider />
        <Group justify="flex-end" mt="md">
          <Button variant="outline" size="md" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            size="md"
            loading={isPending}
            disabled={isPending}
          >
            Save Berth
          </Button>
        </Group>
      </Stack>
    </form>
  )
}
